import React, { useState } from "react";
import { X } from "lucide-react";

// Helper function to safely encode JSON to Base64, handling Unicode characters
export const safeBase64Encode = (obj: any): string => {
  try {
    // Convert the object to a JSON string
    const jsonString = JSON.stringify(obj);

    // Handle Unicode characters properly by using TextEncoder if available
    if (typeof TextEncoder !== "undefined") {
      const encoder = new TextEncoder();
      const data = encoder.encode(jsonString);
      return btoa(String.fromCharCode(...new Uint8Array(data)));
    }

    // Fallback to standard btoa with URI encoding for older browsers
    return btoa(
      encodeURIComponent(jsonString).replace(/%([0-9A-F]{2})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    );
  } catch (error) {
    console.error("Error encoding to Base64:", error);
    // Fallback to simple encoding if an error occurs
    return btoa(JSON.stringify(obj));
  }
};

// Helper function to safely decode Base64 to JSON, handling Unicode characters
export const safeBase64Decode = (base64String: string): any => {
  try {
    // Decode the Base64 string
    const decodedString = atob(base64String);

    // Handle Unicode characters properly
    try {
      // Try to parse as JSON directly
      return JSON.parse(decodedString);
    } catch (parseError) {
      // If direct parsing fails, try decoding URI components
      const decoded = decodeURIComponent(
        Array.from(decodedString)
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(decoded);
    }
  } catch (error) {
    console.error("Error decoding from Base64:", error);
    return null;
  }
};

export interface Connector {
  id: string;
  code: string;
  name: string;
  type: string;
  status: string;
  cloudified?: boolean; // Added cloudified property
  credentials?: CredentialField[]; // Made credentials optional
}

export interface CredentialField {
  key: string;
  example: string;
  description: string;
}

export interface Credentials {
  [key: string]: string | boolean;
}

interface ConnectorCredentialsFormProps {
  title: string;
  connectors: Connector[];
  selectedConnector: Connector | null;
  credentials: Credentials;
  error: string;
  loading: boolean;
  showConnectorSelect?: boolean;
  onConnectorSelect: (connector: Connector) => void;
  onCredentialChange: (key: string, value: string | boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export function ConnectorCredentialsForm({
  title,
  connectors,
  selectedConnector,
  credentials,
  error,
  loading,
  showConnectorSelect = true,
  onConnectorSelect,
  onCredentialChange,
  onSubmit,
  onCancel,
}: ConnectorCredentialsFormProps) {
  const [urlError, setUrlError] = useState<string>("");
  const [hotelIdError, setHotelIdError] = useState<string>("");

  // Function to validate URL
  const validateUrl = (url: string): boolean => {
    // If the connector is cloudified, we don't need to validate the URL
    if (selectedConnector && selectedConnector.cloudified === true) {
      setUrlError("");
      return true;
    }

    if (!url) {
      setUrlError("Connector URL is required");
      return false;
    }

    try {
      new URL(url);
      setUrlError("");
      return true;
    } catch (e) {
      setUrlError("Please enter a valid URL (e.g., https://example.com)");
      return false;
    }
  };

  // Function to validate Hotel ID
  const validateHotelId = (id: string): boolean => {
    if (!id) {
      setHotelIdError("Hotel ID is required");
      return false;
    }

    setHotelIdError("");
    return true;
  };

  // Handle URL change with validation
  const handleUrlChange = (value: string) => {
    // Only validate URL for non-cloudified connectors
    if (selectedConnector && selectedConnector.cloudified !== true) {
      validateUrl(value);
    }
    onCredentialChange("base_url", value);
  };

  // Handle Hotel ID change with validation
  const handleHotelIdChange = (value: string) => {
    validateHotelId(value);
    onCredentialChange("connector_hotel_id", value);
  };

  // Determine if we should show the header and footer (buttons)
  const showHeader = title !== "";
  const showFooter = onSubmit !== undefined && onCancel !== undefined;

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form
        onSubmit={(e) => {
          // Validate fields before submitting
          const hotelId =
            typeof credentials.connector_hotel_id === "string"
              ? credentials.connector_hotel_id
              : "";

          // Always validate Hotel ID
          const isHotelIdValid = validateHotelId(hotelId);

          // Only validate URL for non-cloudified connectors
          let isUrlValid = true;
          if (selectedConnector && !selectedConnector.cloudified) {
            const baseUrl =
              typeof credentials.base_url === "string"
                ? credentials.base_url
                : "";
            isUrlValid = validateUrl(baseUrl);
          }

          if (!isUrlValid || !isHotelIdValid) {
            e.preventDefault();
            return;
          }

          onSubmit(e);
        }}
        className="space-y-4"
      >
        {showConnectorSelect && (
          <div>
            <label className="form-label">Connector Type</label>
            <select
              value={selectedConnector?.id || ""}
              onChange={(e) => {
                const connector = connectors.find(
                  (c) => c.id === e.target.value
                );
                if (connector) {
                  onConnectorSelect(connector);
                }
              }}
              className="form-input"
              required
            >
              <option value="">Select Connector</option>
              {connectors.map((connector) => (
                <option key={connector.id} value={connector.id}>
                  {connector.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedConnector && (
          <div
            className={
              showConnectorSelect ? "border-t border-gray-200 pt-4 mt-4" : ""
            }
          >
            {!showConnectorSelect && (
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <p className="text-sm text-gray-700">
                  {title} for{" "}
                  <span className="font-medium">{selectedConnector.name}</span>
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Base URL field - only show for non-cloudified connectors */}
              {selectedConnector.cloudified !== true && (
                <div>
                  <label className="form-label">Connector URL</label>
                  <input
                    type="text"
                    value={
                      typeof credentials["base_url"] === "string"
                        ? credentials["base_url"]
                        : ""
                    }
                    onChange={(e) => handleUrlChange(e.target.value)}
                    onBlur={(e) => validateUrl(e.target.value)}
                    className={`form-input ${urlError ? "border-red-500" : ""}`}
                    placeholder="https://api.example.com"
                    required
                  />
                  {urlError && (
                    <p className="mt-1 text-sm text-red-500">{urlError}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    The base URL for the connector API
                  </p>
                </div>
              )}

              {/* Hotel Identifier field */}
              <div>
                <label className="form-label">Hotel ID</label>
                <input
                  type="text"
                  value={
                    typeof credentials["connector_hotel_id"] === "string"
                      ? credentials["connector_hotel_id"]
                      : ""
                  }
                  onChange={(e) => handleHotelIdChange(e.target.value)}
                  onBlur={(e) => validateHotelId(e.target.value)}
                  className={`form-input ${
                    hotelIdError ? "border-red-500" : ""
                  }`}
                  placeholder="hotel123"
                  required
                />
                {hotelIdError && (
                  <p className="mt-1 text-sm text-red-500">{hotelIdError}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Your unique hotel identifier in the PMS system
                </p>
              </div>

              {/* Is Primary checkbox */}
              <div className="flex items-center">
                <input
                  id="is_primary"
                  type="checkbox"
                  checked={credentials["is_primary"] === true}
                  onChange={(e) =>
                    onCredentialChange("is_primary", e.target.checked)
                  }
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_primary"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Set as Primary Connector
                </label>
              </div>
              <div className="mt-1 text-sm">
                <p className="text-gray-500">
                  Primary connector will be used as the default for this hotel.
                </p>
                <p className="mt-1 text-amber-600">
                  Note: Only one PMS connector can be primary for a hotel.
                  Setting this as primary will remove primary status from any
                  other PMS connector.
                </p>
              </div>

              {/* Message for cloudified connectors */}
              {selectedConnector.cloudified === true && (
                <div className="mt-4 p-4 bg-blue-50 rounded-md">
                  <p className="text-blue-700">
                    <strong>Cloudified Connector:</strong> This connector uses
                    SFTP for data exchange. No additional credentials are
                    required. An SFTP account will be automatically created for
                    you.
                  </p>
                </div>
              )}

              {/* Original credential fields */}
              {selectedConnector.cloudified !== true &&
                selectedConnector.credentials &&
                selectedConnector.credentials.map((field) => (
                  <div key={field.key}>
                    <label className="form-label">{field.key}</label>
                    <input
                      type={
                        field.key.toLowerCase().includes("password")
                          ? "password"
                          : "text"
                      }
                      value={
                        typeof credentials[field.key] === "string"
                          ? (credentials[field.key] as string)
                          : typeof credentials[field.key] === "boolean"
                          ? String(credentials[field.key])
                          : ""
                      }
                      onChange={(e) =>
                        onCredentialChange(field.key, e.target.value)
                      }
                      className="form-input"
                      placeholder={field.example}
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      {field.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {showFooter && (
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !selectedConnector}
            >
              {loading ? "Processing..." : "Save Credentials"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

// Modal wrapper for the connector credentials form
interface ConnectorCredentialsModalProps extends ConnectorCredentialsFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ConnectorCredentialsModal({
  isOpen,
  onClose,
  ...formProps
}: ConnectorCredentialsModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <ConnectorCredentialsForm
            {...formProps}
            onCancel={() => {
              onClose();
              if (formProps.onCancel) formProps.onCancel();
            }}
          />
        </div>
      </div>
    </div>
  );
}
